<template>
    <UChip class="profile" inset :show="false">

        <UDropdownMenu :items="menuItems" :ui="{ content: 'min-w-[14rem]' }">
            <UButton 
                variant="ghost" 
                color="neutral"
                size="sm"
                class="flex items-center gap-2  rounded-lg"
                >
                <UAvatar 
                    :src="avatarData.userPictureURL" 
                    :alt="avatarData.userName"
                    size="sm"
                    class="ring-2 ring-white dark:ring-gray-700 text-interactive" 
                    :ui="{
                        root: 'text-default dark:bg-accent',
                        fallback: 'text-default text-interactive'
                    }"
                    />
                <span class="text-sm font-medium text-text-primary">{{ avatarData?.displayName || 'User' }}</span>
                <UIcon name="i-heroicons-chevron-down" class="w-4 h-4 text-text-primary" />
            </UButton>

            <template #content-top>
                <div
                    class="border-b border-gray-200 px-3 py-2.5 dark:border-gray-700"
                    @click.stop
                >
                    <div class="mb-3 space-y-1.5">
                        <label class="block text-xs font-medium text-text-secondary">Color theme</label>
                        <USelect
                            v-model="appColorTheme"
                            :items="themeSelectItems"
                            value-key="value"
                            label-key="label"
                            size="sm"
                            class="w-full"
                            aria-label="App color theme"
                        />
                    </div>
                    <div class="space-y-1.5">
                        <label class="block text-xs font-medium text-text-secondary">Appearance</label>
                        <div class="flex items-center gap-0.5 rounded-md border border-gray-200 p-0.5 dark:border-gray-600">
                            <UButton
                                :variant="colorMode.preference === 'light' ? 'solid' : 'ghost'"
                                color="neutral"
                                size="xs"
                                icon="i-heroicons-sun"
                                class="flex-1 rounded-sm"
                                aria-label="Light mode"
                                @click="colorMode.preference = 'light'"
                            />
                            <UButton
                                :variant="colorMode.preference === 'dark' ? 'solid' : 'ghost'"
                                color="neutral"
                                size="xs"
                                icon="i-heroicons-moon"
                                class="flex-1 rounded-sm"
                                aria-label="Dark mode"
                                @click="colorMode.preference = 'dark'"
                            />
                        </div>
                    </div>
                </div>
            </template>
        </UDropdownMenu>
    </UChip>

    <!-- Change Password Modal -->
    <UModal v-model:open="showChangePasswordModal">
        <template #content>
            <div class="p-6">
                <div class="flex items-center gap-3 mb-6">
                    <div class="flex items-center justify-center w-10 h-10 rounded-full bg-gray-100 dark:bg-[var(--color-active-surface)]">
                        <UIcon name="i-heroicons-key" class="w-5 h-5 text-primary" />
                    </div>
                    <div>
                        <h3 class="text-lg font-semibold text-text-primary dark:text-white">Change Password</h3>
                        <p class="text-sm text-text-secondary dark:text-text-secondary">Enter your new password below</p>
                    </div>
                </div>

                <form @submit.prevent="handleChangePassword" class="space-y-5">
                    <UFormField label="New Password" :error="passwordError" required>
                        <UInput
                            v-model="newPassword"
                            type="password"
                            placeholder="Enter new password"
                            size="md"
                            class="w-full"
                            autocomplete="new-password"
                        >
                            <template #leading>
                                <UIcon name="i-heroicons-lock-closed" class="w-4 h-4 text-text-secondary" />
                            </template>
                        </UInput>
                    </UFormField>

                    <UFormField label="Confirm New Password" :error="confirmPasswordError" required>
                        <UInput
                            v-model="confirmPassword"
                            type="password"
                            placeholder="Confirm new password"
                            size="md"
                            class="w-full"
                            autocomplete="new-password"
                        >
                            <template #leading>
                                <UIcon name="i-heroicons-lock-closed" class="w-4 h-4 text-text-secondary" />
                            </template>
                        </UInput>
                    </UFormField>

                    <div v-if="changePasswordError" class="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-4">
                        <div class="flex items-center gap-2 text-sm text-red-600 dark:text-red-400">
                            <UIcon name="i-heroicons-exclamation-triangle-20-solid" class="w-5 h-5 shrink-0" />
                            <span>{{ changePasswordError }}</span>
                        </div>
                    </div>

                    <div class="flex justify-end gap-3 pt-2">
                        <UButton
                            color="neutral"
                            variant="outline"
                            label="Cancel"
                            @click="closeChangePasswordModal"
                            :disabled="isChangingPassword"
                        />
                        <UButton
                            type="submit"
                            color="primary"
                            label="Update Password"
                            :loading="isChangingPassword"
                            :disabled="!isChangePasswordFormValid"
                        />
                    </div>
                </form>
            </div>
        </template>
    </UModal>
</template>

<script setup lang="ts">

const { appColorTheme, options } = useAppColorTheme()
const colorMode = useColorMode()
const requestFetch = useRequestFetch()

const themeSelectItems = computed(() =>
  options.map((o) => ({ label: o.label, value: o.id })),
)

// Get user info from cookie
const USERINFOCOOKIE = useCookie('USERINFO')
const userInfo = computed(() => {
    if (USERINFOCOOKIE.value) {
        try {
            return typeof USERINFOCOOKIE.value === 'string' 
                ? JSON.parse(USERINFOCOOKIE.value) 
                : USERINFOCOOKIE.value
        } catch {
            return null
        }
    }
    return null
})

// AVATAR LOGIC
const avatarData = computed(() => ({
    displayName: (userInfo.value?.username || userInfo.value?.email || 'User').split('@')[0],
    userName: (userInfo.value?.username || userInfo.value?.email || 'User').toUpperCase(),
    userPictureURL: null,
}))

const showChangePasswordModal = ref(false)
const newPassword = ref('')
const confirmPassword = ref('')
const isChangingPassword = ref(false)
const changePasswordError = ref('')
const passwordError = ref('')
const confirmPasswordError = ref('')

const isChangePasswordFormValid = computed(() => {
    return newPassword.value.length >= 1 && confirmPassword.value.length >= 1
})

function openChangePasswordModal() {
    showChangePasswordModal.value = true
    newPassword.value = ''
    confirmPassword.value = ''
    changePasswordError.value = ''
    passwordError.value = ''
    confirmPasswordError.value = ''
}

function closeChangePasswordModal() {
    showChangePasswordModal.value = false
    newPassword.value = ''
    confirmPassword.value = ''
    changePasswordError.value = ''
}

async function handleChangePassword() {
    changePasswordError.value = ''
    passwordError.value = ''
    confirmPasswordError.value = ''

    if (!newPassword.value.trim()) {
        passwordError.value = 'Password is required'
        return
    }
    if (newPassword.value !== confirmPassword.value) {
        confirmPasswordError.value = 'Passwords do not match'
        return
    }

    isChangingPassword.value = true
    try {
        await requestFetch('/api/user/update-password', {
            method: 'POST',
            body: { newpassword: newPassword.value },
        })
        closeChangePasswordModal()
        useToast().add({
            title: 'Password updated',
            description: 'Your password has been changed successfully.',
            color: 'success',
        })
    } catch (err: any) {
        changePasswordError.value = err?.data?.message || err?.statusMessage || 'Failed to update password'
    } finally {
        isChangingPassword.value = false
    }
}

const menuItems = computed(() => [
    [
        {
            label: 'Change password',
            icon: 'i-heroicons-key',
            onSelect: openChangePasswordModal
        },
        {
            label: 'Sign out',
            icon: 'i-heroicons-arrow-left-start-on-rectangle-solid',
            onSelect: () => {
                const JSESSIONIDCOOKIE = useCookie('JSESSIONID')
                JSESSIONIDCOOKIE.value = null
                USERINFOCOOKIE.value = null
                navigateTo('/login')
            }
        }
    ]
])

</script>